<script>
  import { blur } from "svelte/transition";
  import { translate } from "../../translations/index.js";

  const { goToNextStep, close } = $props();

  // Form state using Svelte 5 reactivity
  let name = $state("");
  let email = $state("");
  let newsletter = $state(false);
  let terms = $state(false);
  let isSubmitting = $state(false);

  // Field-specific error states
  let nameError = $state("");
  let emailError = $state("");
  let termsError = $state("");
  let submitError = $state("");

  // Validate a single field
  function validateField(field, value) {
    if (field === "name" && !value) {
      nameError = translate("form.error.name");
      return false;
    } else if (field === "name") {
      nameError = "";
    }

    if (field === "email") {
      if (!value) {
        emailError = translate("form.error.email.required");
        return false;
      } else if (!/^\S+@\S+\.\S+$/.test(value)) {
        emailError = translate("form.error.email.invalid");
        return false;
      } else {
        emailError = "";
      }
    }

    if (field === "terms" && !value) {
      termsError = translate("form.error.terms");
      return false;
    } else if (field === "terms") {
      termsError = "";
    }

    return true;
  }

  const handleError = () => {
    submitError = "Something went wrong while submitting this form. Please try again.";
  };

  // Form submission handler
  async function submitForm() {
    try {
      // Reset errors
      nameError = "";
      emailError = "";
      termsError = "";
      submitError = "";

      // Validate all fields
      const nameValid = validateField("name", name);
      const emailValid = validateField("email", email);
      const termsValid = validateField("terms", terms);

      // If any validation fails, stop submission
      if (!nameValid || !emailValid || !termsValid) {
        return;
      }

      isSubmitting = true;

      // Create FormData object
      const formData = new FormData();
      formData.append("firstName", name);
      formData.append("email", email);
      formData.append("campaign", "swarm_teaser");

      if (newsletter) {
        formData.append("optInTvl", '1');
      }

      // Send POST request
      const response = await fetch("/api-simple/?module=marketing&action=email", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        handleError();
      } else {
        goToNextStep();
      }

      const data = await response.json();

      // Go to next step after successful submission
    } catch (err) {
      handleError();
    } finally {
      isSubmitting = false;
    }
  }
</script>

<div class="form" in:blur={{ duration: 500 }} out:blur={{ duration: 500 }}>
  {#if submitError}
    <div class="error-message">{translate("form.error.submit")}</div>
  {/if}

  <div class="form__inputs">
    <p class="form__inputs__text">
      {translate("form.label.nameAndEmail")}
    </p>

    <div class="form__field">
      <input
        type="text"
        placeholder={translate("form.placeholder.name")}
        bind:value={name}
        onblur={() => validateField("name", name)}
        class:error={nameError}
        required
      />
      {#if nameError}
        <div class="field-error">{nameError}</div>
      {/if}
    </div>

    <div class="form__field">
      <input
        type="email"
        placeholder={translate("form.placeholder.email")}
        bind:value={email}
        onblur={() => validateField("email", email)}
        class:error={emailError}
        required
      />
      {#if emailError}
        <div class="field-error">{emailError}</div>
      {/if}
    </div>
  </div>

  <div class="form__inputs">
    <label class="form__checkbox" for="newsletter">
      <input
        type="checkbox"
        id="newsletter"
        name="newsletter"
        bind:checked={newsletter}
      />
      <span>
        {translate("form.label.newsletter")}
      </span>
    </label>

    <div class="form__checkbox-container">
      <label class="form__checkbox" for="terms">
        <input
          type="checkbox"
          id="terms"
          name="terms"
          bind:checked={terms}
          onchange={() => validateField("terms", terms)}
          class:error={termsError}
          required
        />
        <span>
          {@html translate("form.label.terms")}
        </span>
      </label>
      {#if termsError}
        <div class="field-error checkbox-error">{termsError}</div>
      {/if}
    </div>
  </div>

  <div class="button-wrapper">
    <button class="btn" onclick={submitForm} disabled={isSubmitting}>
      {isSubmitting ? translate("form.button.submitting") : translate("form.button.submit")}
    </button>

    <button class="btn btn-ghost" onclick={() => close(false)} aria-label="Close">
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M6.99914 8.0598L12.4688 13.5294L13.5294 12.4688L8.0598 6.99914L13.5295 1.52941L12.4689 0.46875L6.99914 5.93848L1.52941 0.46875L0.46875 1.52941L5.93848 6.99914L0.468863 12.4688L1.52952 13.5294L6.99914 8.0598Z"
        />
      </svg>
    </button>
  </div>
</div>

<style>
  .form {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 40px;
    width: 100%;
    max-width: 480px;

    @media (orientation: landscape) {
      gap: calc(var(--to-vw) * 40);
      max-width: calc(var(--to-vw) * 480);
    }
  }

  .form input {
    width: 100%;
    padding: 12px 24px;
    border: 1px solid var(--color-yellow);
    border-radius: 80px;
    font-size: 16px;
    background-color: transparent;
    color: var(--color-white);
    box-sizing: border-box;

    @media (orientation: landscape) {
      padding: calc(var(--to-vw) * 12) calc(var(--to-vw) * 24);
      font-size: calc(var(--to-vw) * 14);
    }
  }

  .form input.error {
    border-color: #ff5252;
  }

  .form__inputs {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    box-sizing: border-box;

    @media (orientation: landscape) {
      gap: calc(var(--to-vw) * 12);
    }
  }

  .form__field {
    position: relative;
    width: 100%;
  }

  .form__inputs__text {
    font-size: 20px;
    margin-bottom: 12px;
    font-weight: 500;

    @media (orientation: landscape) {
      font-size: calc(var(--to-vw) * 24);
      margin-bottom: calc(var(--to-vw) * 12);
    }
  }

  .button-wrapper {
    display: flex;
    align-items: center;
    gap: 16px;

    @media (orientation: landscape) {
      gap: calc(var(--to-vw) * 16);
    }
  }

  .form__checkbox-container {
    position: relative;
    width: 100%;
  }

  .form__checkbox {
    display: flex;
    gap: 16px;
    width: 100%;
    box-sizing: border-box;
    line-height: 1.5;

    @media (orientation: landscape) {
      gap: calc(var(--to-vw) * 16);
    }
  }

  .form__checkbox input {
    position: relative;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 1px solid var(--color-white);
    border-radius: 80px;
    background-color: transparent;
    color: var(--color-white);
    cursor: pointer;
    transition: all 0.2s ease;
    appearance: none;

    @media (orientation: landscape) {
      width: calc(var(--to-vw) * 24);
      height: calc(var(--to-vw) * 24);
    }
  }

  .form__checkbox input:checked {
    border-color: var(--color-yellow);
  }

  .form__checkbox input.error {
    border-color: #ff5252;
  }

  .form__checkbox input:checked:after {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    content: "";
    width: 12px;
    height: 12px;
    border-radius: 80px;
    background-color: var(--color-yellow);

    @media (orientation: landscape) {
      width: calc(var(--to-vw) * 12);
      height: calc(var(--to-vw) * 12);
    }
  }

  .form__checkbox span {
    font-size: 16px;

    @media (orientation: landscape) {
      font-size: calc(var(--to-vw) * 14);
    }
  }

  :global(.form__checkbox a) {
    color: var(--color-yellow);
    text-decoration: none;
  }

  :global(.form__checkbox a:hover) {
    text-decoration: underline;
  }

  .error-message {
    color: #ff5252;
    font-size: 16px;
    margin-bottom: 10px;
    width: 100%;

    @media (orientation: landscape) {
      font-size: calc(var(--to-vw) * 14);
      margin-bottom: calc(var(--to-vw) * 10);
    }
  }

  .field-error {
    color: #ff5252;
    font-size: 14px;
    margin-top: 4px;
    padding-left: 24px;

    @media (orientation: landscape) {
      font-size: calc(var(--to-vw) * 12);
      margin-top: calc(var(--to-vw) * 4);
      padding-left: calc(var(--to-vw) * 24);
    }
  }

  .checkbox-error {
    padding-left: 40px;

    @media (orientation: landscape) {
      padding-left: calc(var(--to-vw) * 40);
    }
  }

  button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
</style>
